import type { ReactNode } from 'react';

/**
 * A renderer for the Markdown subset the legal documents actually use.
 *
 * The repository has no Markdown dependency in `packages/web`, and the legal
 * documents are the only Markdown the dashboard has ever needed to show. Adding
 * a parser dependency to satisfy four files would put a third-party HTML
 * pipeline in the path of a page users are asked to agree to; instead this
 * handles the exact constructs those files contain — headings, paragraphs,
 * blockquotes, lists, tables, rules, and inline bold / italic / code / links —
 * and emits React elements, so nothing is ever interpreted as raw HTML.
 */

/** Maps a link target inside a document to one this page can serve. */
export type LinkResolver = (href: string) => string;

const HEADING = /^(#{1,6})\s+(.*)$/;
const RULE = /^-{3,}$/;
const BULLET = /^[-*]\s+/;
const ORDERED = /^\d+\.\s+/;
const TABLE_ROW = /^\|/;
const TABLE_SEPARATOR = /^\|[\s:|-]+\|$/;

interface InlineContext {
  resolveLink: LinkResolver;
  keyPrefix: string;
}

/**
 * Inline markup, scanned left to right.
 *
 * Order matters: `**` is checked before `*`, so bold is never mistaken for two
 * italic spans.
 */
function inline(text: string, ctx: InlineContext): ReactNode[] {
  const nodes: ReactNode[] = [];
  let plain = '';
  let i = 0;
  let n = 0;

  const flush = (): void => {
    if (plain) {
      nodes.push(plain);
      plain = '';
    }
  };

  const push = (node: ReactNode): void => {
    flush();
    nodes.push(node);
  };

  while (i < text.length) {
    const char = text[i];

    if (char === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        push(<code key={`${ctx.keyPrefix}-c${n++}`}>{text.slice(i + 1, end)}</code>);
        i = end + 1;
        continue;
      }
    }

    if (char === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        push(
          <strong key={`${ctx.keyPrefix}-b${n++}`}>
            {inline(text.slice(i + 2, end), { ...ctx, keyPrefix: `${ctx.keyPrefix}-b${n}` })}
          </strong>,
        );
        i = end + 2;
        continue;
      }
    }

    if (char === '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1) {
        push(
          <em key={`${ctx.keyPrefix}-i${n++}`}>
            {inline(text.slice(i + 1, end), { ...ctx, keyPrefix: `${ctx.keyPrefix}-i${n}` })}
          </em>,
        );
        i = end + 1;
        continue;
      }
    }

    if (char === '[') {
      const labelEnd = text.indexOf('](', i + 1);
      if (labelEnd !== -1) {
        const targetEnd = text.indexOf(')', labelEnd + 2);
        if (targetEnd !== -1) {
          const label = text.slice(i + 1, labelEnd);
          const href = ctx.resolveLink(text.slice(labelEnd + 2, targetEnd));
          push(
            <a key={`${ctx.keyPrefix}-a${n++}`} href={href}>
              {inline(label, { ...ctx, keyPrefix: `${ctx.keyPrefix}-a${n}` })}
            </a>,
          );
          i = targetEnd + 1;
          continue;
        }
      }
    }

    plain += char;
    i += 1;
  }

  flush();
  return nodes;
}

function cells(row: string): string[] {
  const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => cell.trim());
}

/** Render a legal document to React elements. */
export function renderMarkdown(markdown: string, resolveLink: LinkResolver): ReactNode[] {
  const lines = markdown.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let block = 0;

  const ctx = (suffix: string): InlineContext => ({
    resolveLink,
    keyPrefix: `md-${block}-${suffix}`,
  });

  while (i < lines.length) {
    const line = lines[i] ?? '';

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      const level = heading[1]?.length ?? 1;
      const body = inline(heading[2] ?? '', ctx('h'));
      const key = `h-${block++}`;
      if (level === 1) blocks.push(<h1 key={key}>{body}</h1>);
      else if (level === 2) blocks.push(<h2 key={key}>{body}</h2>);
      else if (level === 3) blocks.push(<h3 key={key}>{body}</h3>);
      else if (level === 4) blocks.push(<h4 key={key}>{body}</h4>);
      else blocks.push(<h5 key={key}>{body}</h5>);
      i += 1;
      continue;
    }

    if (RULE.test(line.trim())) {
      blocks.push(<hr key={`hr-${block++}`} />);
      i += 1;
      continue;
    }

    // Blockquote: consecutive `>` lines, a bare `>` separating paragraphs.
    if (line.startsWith('>')) {
      const quoted: string[][] = [[]];
      while (i < lines.length && (lines[i] ?? '').startsWith('>')) {
        const body = (lines[i] ?? '').replace(/^>\s?/, '');
        if (body.trim() === '') quoted.push([]);
        else quoted[quoted.length - 1]?.push(body.trim());
        i += 1;
      }
      const paragraphs = quoted
        .filter((paragraph) => paragraph.length > 0)
        .map((paragraph, index) => (
          <p key={`q-${block}-${index}`}>{inline(paragraph.join(' '), ctx(`q${index}`))}</p>
        ));
      blocks.push(
        <blockquote key={`quote-${block++}`} className="legal-doc__quote">
          {paragraphs}
        </blockquote>,
      );
      continue;
    }

    // Table: a header row, a separator row, then body rows.
    if (TABLE_ROW.test(line)) {
      const rows: string[] = [];
      while (i < lines.length && TABLE_ROW.test(lines[i] ?? '')) {
        rows.push(lines[i] ?? '');
        i += 1;
      }
      const header = cells(rows[0] ?? '');
      const body = rows.slice(1).filter((row) => !TABLE_SEPARATOR.test(row.trim()));
      blocks.push(
        <div className="table-wrap" key={`table-${block++}`}>
          <table className="table table--compact">
            <thead>
              <tr>
                {header.map((cell, index) => (
                  <th key={`th-${index}`}>{inline(cell, ctx(`th${index}`))}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, rowIndex) => (
                <tr key={`tr-${rowIndex}`}>
                  {cells(row).map((cell, cellIndex) => (
                    <td key={`td-${cellIndex}`}>{inline(cell, ctx(`td${rowIndex}-${cellIndex}`))}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // List: items at column zero, continuation lines indented beneath them.
    if (BULLET.test(line) || ORDERED.test(line)) {
      const items: string[] = [];
      const ordered = ORDERED.test(line);
      while (i < lines.length) {
        const itemLine = lines[i] ?? '';
        if (itemLine.trim() === '') break;
        if (BULLET.test(itemLine) || ORDERED.test(itemLine)) {
          items.push(itemLine.replace(BULLET, '').replace(ORDERED, ''));
          i += 1;
          continue;
        }
        if (HEADING.test(itemLine) || RULE.test(itemLine.trim()) || TABLE_ROW.test(itemLine) || itemLine.startsWith('>')) break;
        if (items.length > 0 && /^\s+/.test(itemLine)) {
          items[items.length - 1] = `${items[items.length - 1] ?? ''} ${itemLine.trim()}`;
          i += 1;
          continue;
        }
        break;
      }
      const rendered = items.map((item, index) => (
        <li key={`li-${block}-${index}`}>{inline(item, ctx(`li${index}`))}</li>
      ));
      blocks.push(
        ordered ? (
          <ol key={`ol-${block++}`}>{rendered}</ol>
        ) : (
          <ul key={`ul-${block++}`}>{rendered}</ul>
        ),
      );
      continue;
    }

    // Paragraph: lines joined until a blank line or a different block starts.
    const paragraph: string[] = [];
    while (i < lines.length) {
      const body = lines[i] ?? '';
      if (
        body.trim() === '' ||
        HEADING.test(body) ||
        RULE.test(body.trim()) ||
        TABLE_ROW.test(body) ||
        body.startsWith('>') ||
        BULLET.test(body) ||
        ORDERED.test(body)
      ) {
        break;
      }
      paragraph.push(body.trim());
      i += 1;
    }
    blocks.push(<p key={`p-${block++}`}>{inline(paragraph.join(' '), ctx('p'))}</p>);
  }

  return blocks;
}
